/**
 * portalApi.ts — typed fetch wrappers for the conduit-portal-api Worker.
 *
 * Base path is /api/portal. In dev, Vite proxies /api/* to the deployed
 * Worker (or a local `wrangler dev` instance — set VITE_PORTAL_API_PROXY_TARGET
 * to override). In prod, Cloudflare Pages routes /api/* to the same Worker
 * so the portal is fully same-origin.
 *
 * Auth model:
 *   - GETs return 401 { error: { require_pin: true } } when the project's
 *     `client_pin_hash` is set and the caller lacks a valid session cookie.
 *   - POST /auth { pin } sets the cookie on success; subsequent GETs use it.
 *   - All requests use credentials: 'include' so the cookie rides every call.
 */

const BASE = (import.meta.env.VITE_PORTAL_API_BASE as string | undefined) || '/api/portal';

// ============================================================
// Types — mirror the published-subset schema in
// cloudflare/d1/001_published_subset_schema.sql
// ============================================================

export interface PortalProject {
  id: string;
  slug: string;
  name: string;
  project_type: string;
  client_name: string | null;
  city_state: string | null;
  overall_status: string;
  status_banner_visible: 0 | 1;
  target_completion_date: string | null;
  target_completion_visible: 0 | 1;
  construction_value: number | null;
  construction_value_visible: 0 | 1;
  color_tag: string | null;
  client_pin_configured: boolean;
  last_published_at: string;
  /** D-134 — JSON string carrying PM-internal volume counts
   *  (notes / tracked_documents / tracked_plans / pay_apps). These tables
   *  don't push rows to D1; only the counts cross so the portal's counts
   *  strip can render the "behind-scenes work volume" signal. May be null
   *  for projects published before the migration ran. */
  pm_internal_counts_json: string | null;
}

export interface PortalPhase {
  id: string;
  phase_number: number;
  phase_name: string;
  phase_category: string | null;
  status: string;
  percent_complete: number;
  target_start_date: string | null;
  target_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  published_notes: string | null;
  /** D-122 — synthesized server-side as 1 for every row the Worker
   *  returns. The pushed subset already filters by is_visible_to_client
   *  at the source; the Worker writes the constant 1 so the unified
   *  ProjectPage portal-mode visibility filter is a no-op on D1 data. */
  is_visible_to_client: 1;
}

export interface PortalActivityEntry {
  id: string;
  phase_id: string | null;
  /** D-122 — links the entry to a specific task. NULL when the entry
   *  is a free-floating phase update (not tied to a task). ProjectPage
   *  uses it to render the linked-update card under the matching task
   *  inside an expanded phase. cloudPublisher null-outs task_id when
   *  the linked task didn't survive the privacy filter. */
  task_id: string | null;
  permit_id: string | null;
  log_type: string;
  section_key: string | null;
  event_timestamp: string;
  manual_sort_order: number;
  published_summary: string;
  /** D-122 — clean prominent row headline; NULL → row renderer falls
   *  back to "<log_type> on <phase_name>". */
  entry_title: string | null;
  /** D-122 — 0/1 boolean. Red wash on the row when 1. */
  is_urgent: 0 | 1;
  /** D-122 — always 1 in the published subset (the filter at the source
   *  keeps drafts off D1). Stored explicitly so the schema is honest
   *  about row state. */
  is_published: 0 | 1;
  contact_name_text: string | null;
}

export interface PortalTask {
  id: string;
  phase_id: string;
  task_number: number;
  task_text: string;
  task_description: string | null;
  task_group: string | null;
  is_complete: 0 | 1;
  is_critical: 0 | 1;
  due_date: string | null;
  completed_date: string | null;
  published_note: string | null;
  /** D-122 — synthesized server-side as 1. Same rationale as
   *  PortalPhase.is_visible_to_client. */
  is_client_visible: 1;
}

/** D-124 — PM-promoted photo on the portal. cloudPublisher pushes one row
 *  per `documents.is_visible_to_portal = 1` photo into D1 + uploads the
 *  bytes to R2. Each row carries the title the PM authored, the upload
 *  timestamp, and metadata for the gallery; the actual bytes ride the
 *  separate `/photos/:id/raw` endpoint via `portalPhotoRawUrl()`.
 *  D-128 — `album_id` joins the row so the portal can group photos
 *  under their parent album's header. */
export interface PortalPhoto {
  id: string;
  phase_id: string | null;
  album_id: string | null;
  portal_title: string | null;
  uploaded_at: string;
  content_type: string;
  size_bytes: number;
  document_name: string | null;
}

/** D-128 — PM-authored album. Photos with `album_id === this.id` cross
 *  to the portal as a group, rendered under the album's name header.
 *  D-129 — `event_date` is the PM-settable date for the album (e.g.,
 *  the day the work happened). Separate from row-insert time. Nullable. */
export interface PortalAlbum {
  id: string;
  name: string;
  description: string | null;
  phase_id: string | null;
  sub_category: string | null;
  event_date: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PortalPermit {
  id: string;
  phase_id: string | null;
  permit_type: string;
  jurisdiction: string | null;
  permit_number: string | null;
  status: string;
  application_date: string | null;
  issued_date: string | null;
  expiration_date: string | null;
  published_summary: string | null;
}

// ============================================================
// Errors
// ============================================================

export class PortalApiError extends Error {
  status: number;
  requirePin: boolean;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.requirePin = !!(
      body &&
      typeof body === 'object' &&
      'error' in body &&
      (body as { error?: { require_pin?: boolean } }).error?.require_pin
    );
    this.body = body;
  }
}

// ============================================================
// Internal fetch wrapper — all calls send the cookie + parse JSON
// ============================================================

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body, ignore */
  }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: { message?: string } }).error.message ?? `HTTP ${res.status}`
        : `HTTP ${res.status}`;
    throw new PortalApiError(res.status, message, body);
  }
  return body as T;
}

// ============================================================
// Endpoints
// ============================================================

export function getProject(slug: string): Promise<{ project: PortalProject; phases: PortalPhase[] }> {
  return call(`/${encodeURIComponent(slug)}`);
}

export function getActivity(slug: string): Promise<{ activity: PortalActivityEntry[] }> {
  return call(`/${encodeURIComponent(slug)}/activity`);
}

export function getTasks(slug: string): Promise<{ tasks: PortalTask[] }> {
  return call(`/${encodeURIComponent(slug)}/tasks`);
}

export function getPermits(slug: string): Promise<{ permits: PortalPermit[] }> {
  return call(`/${encodeURIComponent(slug)}/permits`);
}

/** D-124 — list PM-promoted photos for one project. Metadata only;
 *  the bytes are streamed via `portalPhotoRawUrl()` below. */
export function getProjectPhotos(slug: string): Promise<{ photos: PortalPhoto[] }> {
  return call(`/${encodeURIComponent(slug)}/photos`);
}

/** D-128 — list PM-authored albums for one project. Photos cluster
 *  under their parent album in the portal gallery render. */
export function getProjectAlbums(slug: string): Promise<{ albums: PortalAlbum[] }> {
  return call(`/${encodeURIComponent(slug)}/albums`);
}

/** D-124 — build the URL the portal `<img>` tags hit for raw photo bytes.
 *  The Worker enforces the PIN cookie + scopes the photo lookup to the
 *  slug, so this URL alone (without the cookie) returns 401. */
export function portalPhotoRawUrl(slug: string, photoId: string): string {
  return `${BASE}/${encodeURIComponent(slug)}/photos/${encodeURIComponent(photoId)}/raw`;
}

// ============================================================
// D-130 — client photo albums (clients create on the portal)
// ============================================================

/** D-130 — client-authored album. Clients group their uploads into
 *  albums via the portal "My photos" tab. Each album is a row in
 *  client_uploaded_albums on D1. */
export interface ClientAlbum {
  id: string;
  name: string;
  event_date: string | null;
  created_at: string;
  photo_count: number;
}

/** D-130 — one photo a client uploaded. Belongs to one ClientAlbum
 *  (album_id) or stays unsorted (legacy from before D-130 — album_id
 *  is null). Bytes ride `clientPhotoRawUrl()`. */
export interface ClientPhoto {
  id: string;
  album_id: string | null;
  uploaded_at: string;
  uploader_label: string | null;
  content_type: string;
  size_bytes: number;
}

export function listClientAlbums(slug: string): Promise<{ albums: ClientAlbum[] }> {
  return call(`/${encodeURIComponent(slug)}/client-albums`);
}

export function createClientAlbum(
  slug: string,
  body: { name: string; event_date?: string | null },
): Promise<{ album: ClientAlbum }> {
  return call(`/${encodeURIComponent(slug)}/client-albums`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function patchClientAlbum(
  slug: string,
  albumId: string,
  body: { name?: string; event_date?: string | null },
): Promise<{ ok: true; changed: boolean }> {
  return call(`/${encodeURIComponent(slug)}/client-albums/${encodeURIComponent(albumId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteClientAlbum(
  slug: string,
  albumId: string,
): Promise<{ ok: true; deleted_photos: number }> {
  return call(`/${encodeURIComponent(slug)}/client-albums/${encodeURIComponent(albumId)}`, {
    method: 'DELETE',
  });
}

export function listClientPhotos(slug: string): Promise<{ photos: ClientPhoto[] }> {
  return call(`/${encodeURIComponent(slug)}/client-photos`);
}

export function deleteClientPhoto(
  slug: string,
  photoId: string,
): Promise<{ ok: true }> {
  return call(`/${encodeURIComponent(slug)}/client-photos/${encodeURIComponent(photoId)}`, {
    method: 'DELETE',
  });
}

/** D-130 — build the URL for raw client-uploaded photo bytes. Same
 *  PIN-gated Worker stream pattern as portalPhotoRawUrl(). */
export function clientPhotoRawUrl(slug: string, photoId: string): string {
  return `${BASE}/${encodeURIComponent(slug)}/client-photos/${encodeURIComponent(photoId)}/raw`;
}

export function authenticate(slug: string, pin: string): Promise<{ ok: true; no_pin_required?: boolean }> {
  return call(`/${encodeURIComponent(slug)}/auth`, {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

// ---- E.7.4 — Client photo uploads ----

export interface PortalUpload {
  id: string;
  phase_id: string | null;
  uploaded_at: string;
  uploader_label: string | null;
  content_type: string;
  size_bytes: number;
}

export interface UploadPhotoResult {
  ok: true;
  photo: PortalUpload & { project_id: string; r2_key: string };
}

/**
 * Upload a photo through the portal Worker → R2 → D1 metadata.
 * Returns the new photo metadata. The PM (Conduit) will pull it down on
 * their next "Pull client uploads" run.
 */
export async function uploadPortalPhoto(
  slug: string,
  file: File,
  opts: { phaseId?: string | null; albumId?: string | null; caption?: string | null } = {},
): Promise<UploadPhotoResult> {
  const fd = new FormData();
  fd.append('photo', file);
  if (opts.phaseId) fd.append('phase_id', opts.phaseId);
  if (opts.albumId) fd.append('album_id', opts.albumId);
  if (opts.caption) fd.append('caption', opts.caption);
  const res = await fetch(`${BASE}/${encodeURIComponent(slug)}/upload`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: { message?: string } }).error.message ?? `HTTP ${res.status}`
        : `HTTP ${res.status}`;
    throw new PortalApiError(res.status, message, body);
  }
  return body as UploadPhotoResult;
}

export function listPortalUploads(slug: string): Promise<{ uploads: PortalUpload[] }> {
  return call(`/${encodeURIComponent(slug)}/uploads`);
}

export const MAX_PORTAL_UPLOAD_BYTES = 50 * 1024 * 1024;
